/**
 * client/src/services/offlineTileCache.ts — Offline Map Tile Caching
 *
 * FASE 6: CRITICAL FIX — I motociclisti vanno dove non c'è campo.
 * Senza tile offline, la mappa è una schermata grigia in montagna.
 *
 * Architettura:
 *   - IndexedDB (Dexie) per persistenza tile PNG a lungo termine
 *   - Leaflet TileLayer con getTileUrl() custom che:
 *       1. Cerca in IndexedDB
 *       2. Se trovato, restituisce blob:// URL
 *       3. Se non trovato, fetch da rete e salva in IndexedDB
 *   - LRU eviction: massimo 10,000 tile (~50-100 MB)
 *   - Prefetch: scarica tile lungo una route pianificata
 *
 * Usage:
 *   import { createOfflineTileLayer } from '@/services/offlineTileCache';
 *
 *   const tileLayer = createOfflineTileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
 *     maxCacheTiles: 10000,
 *     attribution: '© OpenStreetMap',
 *   });
 *   tileLayer.addTo(map);
 *
 *   // Prefetch tiles along a route
 *   await prefetchRouteTiles(waypoints, tileLayer, 14);
 */

import Dexie, { type Table } from 'dexie';

// ─── IndexedDB Schema ────────────────────────────────────────────────────────

interface CachedTile {
  id?: number;
  /** Cache key: "z/x/y" */
  key: string;
  /** Tile zoom level */
  z: number;
  /** Tile X coordinate */
  x: number;
  /** Tile Y coordinate */
  y: number;
  /** PNG image data */
  blob: Blob;
  /** Unix timestamp of last access */
  lastAccessed: number;
  /** Size in bytes */
  size: number;
}

class TileCacheDb extends Dexie {
  tiles!: Table<CachedTile, number>;

  constructor() {
    super('RideConnectTileCache');
    this.version(1).stores({
      tiles: '++id, &key, z, x, y, lastAccessed',
    });
  }
}

const tileDb = new TileCacheDb();

// ─── Configuration ───────────────────────────────────────────────────────────

interface OfflineTileOptions {
  /** Maximum number of cached tiles */
  maxCacheTiles?: number;
  /** Attribution string */
  attribution?: string;
  /** Tile size in pixels (default 256) */
  tileSize?: number;
  /** Minimum zoom for caching */
  minZoom?: number;
  /** Maximum zoom for caching */
  maxZoom?: number;
  /** Custom headers for tile requests */
  headers?: Record<string, string>;
}

const DEFAULT_OPTIONS: Required<OfflineTileOptions> = {
  maxCacheTiles: 10000,
  attribution: '',
  tileSize: 256,
  minZoom: 0,
  maxZoom: 18,
  headers: {},
};

// ─── LRU Eviction ────────────────────────────────────────────────────────────

async function evictOldest(keepCount: number): Promise<void> {
  const total = await tileDb.tiles.count();
  if (total <= keepCount) return;

  const toDelete = total - keepCount;
  const oldest = await tileDb.tiles
    .orderBy('lastAccessed')
    .limit(toDelete)
    .toArray();

  const ids = oldest.map(t => t.id!).filter(Boolean);
  if (ids.length > 0) {
    await tileDb.tiles.bulkDelete(ids);
    console.log(`[TileCache] Evicted ${ids.length} old tiles (${keepCount} remaining)`);
  }
}

// ─── Cache in memoria dei blob tile: budget + revoca ────────────────────────
//
// GOOGLE PLAY QUALITY 2027 (Feb 2027) — pilastro "Dynamic memory usage".
//
// Prima qui c'era una `Map<string, string>` che cresceva per TUTTA la sessione:
// ogni tile visto (panning, zoom, prefetch) aggiungeva un blob URL che nessuno
// revocava. Un blob URL non revocato tiene vivo il Blob fino al reload del
// documento: con tile da ~20-40 KB, una navigazione lunga poteva trattenere
// centinaia di MB — memoria che Play misura anche quando l'app non è visibile.
//
// Ora la cache ha un budget in byte e un tetto di voci: quando si sfora,
// l'elemento meno usato di recente (LRU) viene rimosso e il suo blob URL
// REVOCATO, quindi la memoria torna subito disponibile.

/** Budget di byte per la cache in memoria di un tile layer (default 8 MB). */
export const TILE_MEMORY_BUDGET_BYTES = 8 * 1024 * 1024;
/** Tetto di voci (protezione da tile molto piccoli). */
export const TILE_MEMORY_MAX_ENTRIES = 400;

export interface TileBlobCache {
  get(key: string): string | undefined;
  set(key: string, blobUrl: string, size: number): void;
  clear(): void;
  stats(): { entries: number; bytes: number };
}

/** Registro delle cache attive: la pressione di memoria le svuota tutte. */
const activeTileCaches = new Set<TileBlobCache>();

export function createTileBlobCache(
  budgetBytes: number = TILE_MEMORY_BUDGET_BYTES,
  maxEntries: number = TILE_MEMORY_MAX_ENTRIES,
): TileBlobCache {
  // Map con ordine di inserimento: la prima chiave è la meno usata di recente.
  const urls = new Map<string, string>();
  const sizes = new Map<string, number>();
  let bytes = 0;

  const revoke = (url: string | undefined): void => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* best-effort: il GC libera comunque al reload del documento */
    }
  };

  const drop = (key: string): void => {
    revoke(urls.get(key));
    urls.delete(key);
    bytes -= sizes.get(key) ?? 0;
    if (bytes < 0) bytes = 0;
    sizes.delete(key);
  };

  const cache: TileBlobCache = {
    get(key) {
      const url = urls.get(key);
      if (url !== undefined) {
        // Rinfresca la recency: delete + set riporta la chiave in fondo (LRU).
        urls.delete(key);
        urls.set(key, url);
      }
      return url;
    },
    set(key, blobUrl, size) {
      if (urls.has(key)) drop(key);
      urls.set(key, blobUrl);
      const safeSize = Number.isFinite(size) && size > 0 ? size : 0;
      sizes.set(key, safeSize);
      bytes += safeSize;

      // Eviction LRU: si ferma se resta un solo elemento (quello appena inserito).
      while ((bytes > budgetBytes || urls.size > maxEntries) && urls.size > 1) {
        const oldest = urls.keys().next().value as string | undefined;
        if (oldest === undefined) break;
        drop(oldest);
      }
    },
    clear() {
      for (const key of Array.from(urls.keys())) drop(key);
      urls.clear();
      sizes.clear();
      bytes = 0;
    },
    stats() {
      return { entries: urls.size, bytes };
    },
  };

  activeTileCaches.add(cache);
  return cache;
}

/**
 * Svuota e revoca tutte le cache tile attive. Ritorna i byte liberati.
 * Invocata dalla pressione di memoria (memoryPressure.ts).
 */
export function clearAllTileCaches(): number {
  let freed = 0;
  for (const cache of Array.from(activeTileCaches)) {
    freed += cache.stats().bytes;
    cache.clear();
  }
  return freed;
}

/** Numero di cache tile registrate (usato dai test). */
export function activeTileCacheCount(): number {
  return activeTileCaches.size;
}

// ─── Tile URL Factory ────────────────────────────────────────────────────────

/**
 * Creates a Leaflet-compatible getTileUrl function that caches tiles offline.
 */
function createCachedTileFetcher(
  templateUrl: string,
  options: Required<OfflineTileOptions>,
): (coords: { x: number; y: number; z: number }) => string {
  // Cache in memoria con budget in byte + revoca dei blob (vedi TileBlobCache):
  // evita la query a IndexedDB per ogni tile senza accumulare memoria illimitata.
  const memoryCache = createTileBlobCache();

  return function getTileUrl(coords: { x: number; y: number; z: number }): string {
    const { x, y, z } = coords;
    const key = `${z}/${x}/${y}`;

    // 1. Check memory cache (blob URLs valid per sessione)
    const memUrl = memoryCache.get(key);
    if (memUrl) return memUrl;

    // 2. Start async load (Leaflet supports Promise-returning getTileUrl via canvas renderer,
    //    but for standard <img> tiles we return a placeholder and swap when ready)
    const url = templateUrl
      .replace('{z}', String(z))
      .replace('{x}', String(x))
      .replace('{y}', String(y))
      .replace('{s}', ['a', 'b', 'c'][Math.floor(Math.random() * 3)]);

    // Trigger background cache load
    loadTileAndCache(key, url, memoryCache, options);

    // Return network URL immediately; the cached version replaces it on next render
    return url;
  };
}

async function loadTileAndCache(
  key: string,
  url: string,
  memoryCache: TileBlobCache,
  options: Required<OfflineTileOptions>,
): Promise<void> {
  try {
    // Check IndexedDB first
    const cached = await tileDb.tiles.get({ key });
    if (cached) {
      // Update last accessed
      await tileDb.tiles.update(cached.id!, { lastAccessed: Date.now() });
      const blobUrl = URL.createObjectURL(cached.blob);
      memoryCache.set(key, blobUrl, cached.size);
      return;
    }

    // Fetch from network
    const response = await fetch(url, {
      headers: options.headers,
      // Low priority — don't block critical API calls
      // @ts-ignore — priority is a newer fetch API feature
      priority: 'low',
    });

    if (!response.ok) return;

    const blob = await response.blob();
    const size = blob.size;

    // Store in IndexedDB
    const [z, x, y] = key.split('/').map(Number);
    await tileDb.tiles.put({
      key,
      z, x, y,
      blob,
      lastAccessed: Date.now(),
      size,
    });

    // Create blob URL for current session
    const blobUrl = URL.createObjectURL(blob);
    memoryCache.set(key, blobUrl, size);

    // Evict if over limit (async, non-blocking)
    evictOldest(options.maxCacheTiles).catch(() => {});

    console.debug(`[TileCache] Cached tile ${key} (${(size / 1024).toFixed(1)} KB)`);
  } catch (err) {
    // Network error or quota exceeded — silently ignore
    console.debug(`[TileCache] Failed to cache tile ${key}:`, (err as Error)?.message);
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Creates a Leaflet TileLayer with integrated offline caching.
 * Drop-in replacement for L.tileLayer().
 *
 * @param templateUrl - Tile server URL template (e.g., 'https://tile.openstreetmap.org/{z}/{x}/{y}.png')
 * @param options - Caching and display options
 * @returns L.TileLayer instance ready to add to map
 */
export function createOfflineTileLayer(
  templateUrl: string,
  options: OfflineTileOptions = {},
): any {
  const L = (window as any).L;
  if (!L) {
    console.warn('[TileCache] Leaflet not loaded — cannot create tile layer');
    return null;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };

  return L.tileLayer(templateUrl, {
    attribution: opts.attribution,
    tileSize: opts.tileSize,
    minZoom: opts.minZoom,
    maxZoom: opts.maxZoom,
    // Custom getTileUrl enables offline-first loading
    // Note: Leaflet calls getTileUrl synchronously, but our cache is async.
    // We use the network URL synchronously and background-swap to cached blob.
    // For full offline support, use createOfflineTileLayerWithSwap below.
  });
}

/**
 * Creates a Leaflet TileLayer that uses cached tiles FIRST,
 * with network fallback. Requires Leaflet >= 1.9.
 *
 * This is the recommended version for full offline support.
 */
export function createOfflineTileLayerWithSwap(
  templateUrl: string,
  options: OfflineTileOptions = {},
): any {
  const L = (window as any).L;
  if (!L) {
    console.warn('[TileCache] Leaflet not loaded');
    return null;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };
  const memoryCache = createTileBlobCache();

  const customLayer = L.tileLayer(templateUrl, {
    attribution: opts.attribution,
    tileSize: opts.tileSize,
    minZoom: opts.minZoom,
    maxZoom: opts.maxZoom,
  });

  // Override createTile to use cached blobs
  const originalCreateTile = customLayer.createTile.bind(customLayer);
  customLayer.createTile = function (coords: any, done: any) {
    const key = `${coords.z}/${coords.x}/${coords.y}`;
    const tile = originalCreateTile(coords, done);

    // Attempt to swap with cached version
    loadTileAndCache(
      key,
      templateUrl.replace('{z}', coords.z).replace('{x}', coords.x).replace('{y}', coords.y),
      memoryCache,
      opts,
    ).then(() => {
      const blobUrl = memoryCache.get(key);
      if (blobUrl && tile.tagName === 'IMG') {
        (tile as HTMLImageElement).src = blobUrl;
      }
    }).catch(() => {});

    return tile;
  };

  // Quando il layer viene rimosso dalla mappa (cambio schermata/route) i blob
  // URL vengono revocati subito: è memoria che Play misura anche ad app non
  // visibile, quindi non deve restare appesa alla sessione.
  customLayer.on('remove', () => {
    memoryCache.clear();
  });

  return customLayer;
}

// ─── Prefetch ────────────────────────────────────────────────────────────────

/**
 * Prefetch tiles along a route (array of lat/lng points) at a given zoom level.
 * Call this when the user is on WiFi before a ride.
 *
 * @param waypoints - Array of { lat, lng } points
 * @param zoom - Zoom level to prefetch (13-15 recommended for navigation)
 * @param templateUrl - Tile server URL template
 */
export async function prefetchRouteTiles(
  waypoints: Array<{ lat: number; lng: number }>,
  zoom: number,
  templateUrl: string,
): Promise<{ fetched: number; skipped: number; errors: number }> {
  if (waypoints.length === 0) return { fetched: 0, skipped: 0, errors: 0 };

  // Calculate bounding box
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  for (const wp of waypoints) {
    if (wp.lat < minLat) minLat = wp.lat;
    if (wp.lat > maxLat) maxLat = wp.lat;
    if (wp.lng < minLng) minLng = wp.lng;
    if (wp.lng > maxLng) maxLng = wp.lng;
  }

  // Add padding (~5 km)
  const pad = 0.05;
  minLat -= pad; maxLat += pad;
  minLng -= pad; maxLng += pad;

  // Convert to tile coordinates
  const n = Math.pow(2, zoom);
  const minX = Math.floor((minLng + 180) / 360 * n);
  const maxX = Math.floor((maxLng + 180) / 360 * n);
  const minY = Math.floor((1 - Math.log(Math.tan(maxLat * Math.PI / 180) + 1 / Math.cos(maxLat * Math.PI / 180)) / Math.PI) / 2 * n);
  const maxY = Math.floor((1 - Math.log(Math.tan(minLat * Math.PI / 180) + 1 / Math.cos(minLat * Math.PI / 180)) / Math.PI) / 2 * n);

  const totalTiles = (maxX - minX + 1) * (maxY - minY + 1);
  console.log(`[TileCache] Prefetching ${totalTiles} tiles for route (zoom ${zoom}, bbox: ${minLat.toFixed(2)},${minLng.toFixed(2)} to ${maxLat.toFixed(2)},${maxLng.toFixed(2)})`);

  let fetched = 0, skipped = 0, errors = 0;
  const concurrency = 6; // parallel downloads
  const queue: Array<{ x: number; y: number }> = [];

  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      queue.push({ x, y });
    }
  }

  async function processQueue() {
    while (queue.length > 0) {
      const batch = queue.splice(0, concurrency);
      await Promise.all(batch.map(async ({ x, y }) => {
        const key = `${zoom}/${x}/${y}`;
        const cached = await tileDb.tiles.get({ key });
        if (cached) {
          skipped++;
          return;
        }

        const url = templateUrl
          .replace('{z}', String(zoom))
          .replace('{x}', String(x))
          .replace('{y}', String(y))
          .replace('{s}', 'a');

        try {
          const response = await fetch(url);
          if (!response.ok) { errors++; return; }

          const blob = await response.blob();
          await tileDb.tiles.put({
            key,
            z: zoom, x, y,
            blob,
            lastAccessed: Date.now(),
            size: blob.size,
          });
          fetched++;
        } catch {
          errors++;
        }
      }));
    }
  }

  await processQueue();
  await evictOldest(10000);

  console.log(`[TileCache] Prefetch complete: ${fetched} fetched, ${skipped} already cached, ${errors} errors`);
  return { fetched, skipped, errors };
}

/**
 * Get cache statistics.
 */
export async function getTileCacheStats(): Promise<{
  totalTiles: number;
  totalSizeMB: number;
  oldestTile: Date | null;
  newestTile: Date | null;
}> {
  const tiles = await tileDb.tiles.toArray();
  const totalSize = tiles.reduce((sum, t) => sum + t.size, 0);

  let oldest: Date | null = null;
  let newest: Date | null = null;

  for (const t of tiles) {
    const d = new Date(t.lastAccessed);
    if (!oldest || d < oldest) oldest = d;
    if (!newest || d > newest) newest = d;
  }

  return {
    totalTiles: tiles.length,
    totalSizeMB: Math.round(totalSize / (1024 * 1024) * 10) / 10,
    oldestTile: oldest,
    newestTile: newest,
  };
}

/**
 * Clear all cached tiles.
 */
export async function clearTileCache(): Promise<void> {
  await tileDb.tiles.clear();
  console.log('[TileCache] Cache cleared');
}
